import { create } from 'zustand';
import { SourcingInternalStore } from 'modules/sourcing-internal/stores/types';
import { defaultSourcingInternalStoreValues } from 'modules/sourcing-internal/stores/constants';
import { handleSourcingSharedSelectedCandidatesNextController } from 'modules/sourcing-shared/controllers/store-controllers/handleSourcingSharedSelectedCandidatesNextController';
import { handleSourcingSharedChooseChannelNextController } from 'modules/sourcing-shared/controllers/store-controllers/handleSourcingSharedChooseChannelNextController';
import { createSharedSourcingStoreMethods } from 'modules/sourcing-shared/shared-stores';
import { scrollToTop } from 'common';
import { createApolloClient } from 'core/services/apollo/createApolloClient';
import {
  GetCandidateBatchDocument,
  DeleteSearchCandidateProfileSelectedItemDocument,
  UpdateSearchCandidateProfileSelectedItemDocument
} from 'generated-graphql/graphql';
import { extractQueryGetItem } from 'core/utils/apollo/extractGetItem';

export const useSourcingInternalStore = create<SourcingInternalStore>()(
  (set, get) => ({
    ...defaultSourcingInternalStoreValues,
    onInit: ({ sessionId, candidateBatch }) => {
      set({
        ...defaultSourcingInternalStoreValues,
        sessionId,
        initialCandidateBatchId: candidateBatch?.id || null,
        candidateBatch,
        step: candidateBatch ? 'SELECTED_CANDIDATES' : 'CHOOSE_ANALYSIS'
      });
    },
    // Header actions
    onHeaderSelectCandidates: () => {
      scrollToTop();
      set({
        step: 'SELECTED_CANDIDATES'
      });
    },
    onHeaderSelectInviteToApply: () => {
      scrollToTop();
      set({
        step: 'SELECTED_CANDIDATES'
      });
    },
    // Step actions
    onSelectAnalysis: async (candidateBatchId) => {
      const currentState = get();

      // If we already have this candidate batch in the store, just change the step
      if (currentState.candidateBatch?.id === candidateBatchId) {
        set({
          step: 'SELECTED_CANDIDATES'
        });
        return;
      }

      // fetch candidate batch
      const client = createApolloClient();

      const { data } = await client.query({
        query: GetCandidateBatchDocument,
        variables: {
          id: candidateBatchId
        },
        fetchPolicy: 'network-only' // Always fetch fresh data
      });

      const candidateBatch = extractQueryGetItem(data);

      if (!candidateBatch) {
        return;
      }

      // Create a new object reference to ensure re-renders
      set({
        step: 'SELECTED_CANDIDATES',
        candidateBatch: { ...candidateBatch }
      });
    },
    onSelectAnalysisBack: () => {
      // TODO: Implement back navigation
    },
    onSelectedCandidatesNext: (props) => {
      const { selectedCandidateProfiles, ...restProps } = props;

      // Set the selected candidate profiles in the store with customInvitationMessage
      set({
        selectedCandidateProfiles: selectedCandidateProfiles.map((profile) => ({
          ...profile,
          customInvitationMessage: null
        }))
      });

      // Continue with the normal flow
      handleSourcingSharedSelectedCandidatesNextController({
        ...restProps,
        get,
        set
      });
    },
    onSelectedCandidatesBack: () => {
      set({
        step: 'CHOOSE_ANALYSIS'
      });
    },
    onRemoveSelectedCandidate: async (selectionId) => {
      const client = createApolloClient();
      await client.mutate({
        mutation: DeleteSearchCandidateProfileSelectedItemDocument,
        variables: {
          input: {
            id: selectionId
          }
        }
      });
    },
    ...createSharedSourcingStoreMethods({
      get,
      set
    }),
    onChooseChannelNext: async ({
      formData,
      shouldClearCustomInvitationMessages
    }) => {
      // Clear custom invitation messages for all candidates only if channel changed
      if (shouldClearCustomInvitationMessages) {
        const client = createApolloClient();
        const { selectedCandidateProfiles } = get();

        // Clear shared message state
        set({
          defaultStartingTemplatedMessage: null,
          customizeMessageFormData: null
        });

        if (selectedCandidateProfiles) {
          // Clear custom invitation messages in the store
          set({
            selectedCandidateProfiles: selectedCandidateProfiles.map(
              (profile) => ({
                ...profile,
                customInvitationMessage: null
              })
            )
          });

          // Update the backend to clear custom invitation messages and relevance
          const promises = selectedCandidateProfiles.map((profile) =>
            client.mutate({
              mutation: UpdateSearchCandidateProfileSelectedItemDocument,
              variables: {
                input: {
                  id: profile.id,
                  customInvitationMessage: null,
                  candidateRelevance: null
                }
              }
            })
          );

          await Promise.all(promises);
        }
      }

      // Continue with the shared controller logic
      await handleSourcingSharedChooseChannelNextController({
        channelFormData: formData,
        get,
        set
      });
    },
    updateSelectedRowCustomMessage: (candidateId, customMessage) => {
      set((state) => {
        const newSelectedCandidateProfiles = [
          ...(state.selectedCandidateProfiles || [])
        ];

        const index = newSelectedCandidateProfiles.findIndex(
          (profile) => profile.id === candidateId
        );

        if (index !== -1) {
          newSelectedCandidateProfiles[index].customInvitationMessage =
            customMessage;
        }

        return { selectedCandidateProfiles: newSelectedCandidateProfiles };
      });
    },
    setStep: (step) => {
      scrollToTop();
      set({ step });
    }
  })
);
